/**
 * ConnectionRetry - Componente para tratamento de erros de conexão
 * Detecta erros 503/5xx e tenta reconexão automática com backoff exponencial
 */

import { useState, useEffect, useCallback, ReactNode } from "react";
import { AlertCircle, RefreshCw, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

interface ConnectionRetryProps {
  children: ReactNode;
  error: Error | null;
  isLoading?: boolean;
  onRetry: () => void;
  maxRetries?: number;
  baseDelay?: number;
}

// Detecta se é erro de conexão/backend
export function isConnectionError(error: unknown): boolean {
  if (!error) return false;
  
  const message = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
  
  return (
    message.includes("503") ||
    message.includes("502") ||
    message.includes("500") ||
    message.includes("network") ||
    message.includes("failed to fetch") ||
    message.includes("upstream connect error") ||
    message.includes("service unavailable") ||
    message.includes("timeout") ||
    message.includes("econnrefused") ||
    message.includes("connection refused")
  );
}

export function ConnectionRetry({
  children,
  error,
  isLoading = false,
  onRetry,
  maxRetries = 3,
  baseDelay = 2000,
}: ConnectionRetryProps) {
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const calculateDelay = useCallback((attempt: number): number => {
    // Backoff exponencial: 2s, 4s, 8s...
    return Math.min(baseDelay * Math.pow(2, attempt), 30000);
  }, [baseDelay]);

  const handleAutoRetry = useCallback(() => {
    if (retryCount >= maxRetries) return;

    const delay = calculateDelay(retryCount);
    setCountdown(delay / 1000);
    setIsRetrying(true);

    // Countdown visual
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Retry após delay
    setTimeout(() => {
      setRetryCount((prev) => prev + 1);
      setIsRetrying(false);
      onRetry();
    }, delay);

    return () => clearInterval(countdownInterval);
  }, [retryCount, maxRetries, calculateDelay, onRetry]);

  const handleManualRetry = () => {
    setRetryCount(0);
    setIsRetrying(false);
    setCountdown(0);
    onRetry();
  };

  // Auto-retry quando erro de conexão é detectado
  useEffect(() => {
    if (error && isConnectionError(error) && retryCount < maxRetries && !isRetrying) {
      handleAutoRetry();
    }
  }, [error, retryCount, maxRetries, isRetrying, handleAutoRetry]);

  // Reset ao resolver o erro
  useEffect(() => {
    if (!error && !isLoading) {
      setRetryCount(0);
      setIsRetrying(false);
      setCountdown(0);
    }
  }, [error, isLoading]);

  // Se houver erro de conexão, mostrar UI de retry
  if (error && isConnectionError(error)) {
    const progress = ((countdown / (calculateDelay(retryCount) / 1000)) * 100);
    const hasExhaustedRetries = retryCount >= maxRetries;

    return (
      <div className="flex items-center justify-center min-h-[200px] p-4">
        <Card className="max-w-md w-full p-6 text-center space-y-4">
          <div className="flex justify-center">
            {isRetrying ? (
              <RefreshCw className="h-12 w-12 text-primary animate-spin" />
            ) : (
              <WifiOff className="h-12 w-12 text-muted-foreground" />
            )}
          </div>
          
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-foreground">
              {hasExhaustedRetries 
                ? "Servidor temporariamente indisponível"
                : "Reconectando ao servidor..."}
            </h3>
            <p className="text-sm text-muted-foreground">
              {hasExhaustedRetries
                ? "O servidor está passando por instabilidade. Tente novamente em alguns minutos."
                : `Tentativa ${retryCount + 1} de ${maxRetries}`}
            </p>
          </div>

          {isRetrying && !hasExhaustedRetries && (
            <div className="space-y-2">
              <Progress value={100 - progress} className="h-2" />
              <p className="text-xs text-muted-foreground">
                Próxima tentativa em {countdown}s
              </p>
            </div>
          )}

          <Button 
            onClick={handleManualRetry}
            variant={hasExhaustedRetries ? "default" : "outline"}
            className="w-full"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Tentar Novamente
          </Button>

          {hasExhaustedRetries && (
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              <AlertCircle className="h-3 w-3" />
              Erro: {error.message}
            </p>
          )}
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}

// Hook para usar em qualquer lugar
export function useConnectionRetry() {
  const [retryKey, setRetryKey] = useState(0);

  const triggerRetry = useCallback(() => {
    setRetryKey((prev) => prev + 1);
  }, []);

  return { retryKey, triggerRetry };
}
